/**
 * Deal Momentum service - Phase 8
 */
import api from './api';
import { ApiResponse } from '../types';
import {
  DealMomentum,
  MomentumListFilters,
  HotDealEntry,
  MomentumDashboard,
  MomentumTimelineResponse,
  CreateMomentumEventRequest,
  MomentumEvent,
  RefreshMomentumRequest,
} from '../types/dealMomentum';

function errMsg(e: any, fallback: string): string {
  return e?.response?.data?.detail || e?.message || fallback;
}

export async function fetchSignals(
  filters?: MomentumListFilters
): Promise<ApiResponse<DealMomentum[]>> {
  try {
    const res = await api.get('/momentum/signals', { params: filters });
    const payload = res.data;
    if (Array.isArray(payload)) return { data: payload };
    return { data: payload?.signals || payload?.items || [] };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to load momentum signals') };
  }
}

export async function fetchDashboard(): Promise<ApiResponse<MomentumDashboard>> {
  try {
    const res = await api.get('/momentum/dashboard');
    return { data: res.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to load momentum dashboard') };
  }
}

export async function fetchHotDeals(
  limit: number = 20
): Promise<ApiResponse<HotDealEntry[]>> {
  try {
    const res = await api.get('/momentum/hot-deals', { params: { limit } });
    const payload = res.data;
    if (Array.isArray(payload)) return { data: payload };
    return { data: payload?.deals || payload?.items || [] };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to load hot deals') };
  }
}

export async function fetchTimeline(params: {
  startup_id?: number;
  project_id?: number;
  days?: number;
}): Promise<ApiResponse<MomentumTimelineResponse>> {
  try {
    const res = await api.get('/momentum/timeline', {
      params: { days: 30, ...params },
    });
    const payload = res.data;
    if (Array.isArray(payload)) {
      return {
        data: {
          startup_id: params.startup_id,
          project_id: params.project_id,
          days: params.days ?? 30,
          points: payload,
        },
      };
    }
    return {
      data: {
        startup_id: payload?.startup_id ?? params.startup_id,
        project_id: payload?.project_id ?? params.project_id,
        days: payload?.days ?? params.days ?? 30,
        points: payload?.points || payload?.timeline || [],
      },
    };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to load timeline') };
  }
}

export async function recordEvent(
  req: CreateMomentumEventRequest
): Promise<ApiResponse<MomentumEvent>> {
  try {
    const res = await api.post('/momentum/events', req);
    return { data: res.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to record event') };
  }
}

export async function refresh(
  params: RefreshMomentumRequest
): Promise<ApiResponse<DealMomentum>> {
  try {
    const res = await api.post('/momentum/refresh', null, { params });
    return { data: res.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to refresh momentum') };
  }
}

export async function refreshAll(): Promise<ApiResponse<{ refreshed: number }>> {
  try {
    const res = await api.post('/momentum/refresh-all');
    return { data: res.data || { refreshed: 0 } };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to refresh all') };
  }
}

const momentumService = {
  fetchSignals,
  fetchDashboard,
  fetchHotDeals,
  fetchTimeline,
  recordEvent,
  refresh,
  refreshAll,
};

export default momentumService;
